import { useEffect, useState } from 'react';
import { Icon, type IconName } from './Icon';

/**
 * 화면 테마 전환 (컨셉 §7.1 · 루브릭 R1-7).
 *
 * 시스템 → 라이트 → 다크 순서로 돈다. 선택은 localStorage('aitex.theme') 에 두고
 * <html data-theme> 로 적용한다 — 시스템이면 속성을 지워 CSS 의 prefers-color-scheme 이 정한다.
 * 첫 렌더 전 적용은 index.html 의 인라인 스크립트가 한다(깜빡임 방지).
 * 저장소가 막힌 환경(시크릿 창 등)에서도 이번 화면에서는 전환이 된다.
 */
export type ThemeChoice = 'system' | 'light' | 'dark';

const KEY = 'aitex.theme';
const ORDER: ThemeChoice[] = ['system', 'light', 'dark'];
const LABEL: Record<ThemeChoice, string> = { system: '시스템 설정', light: '라이트', dark: '다크' };
const TO: Record<ThemeChoice, string> = { system: '시스템 설정으로', light: '라이트로', dark: '다크로' };
const ICON: Record<ThemeChoice, IconName> = { system: 'monitor', light: 'sun', dark: 'moon' };

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

/** 테마가 바뀌는 순간에는 전환 효과를 끈다 — 버튼 · 카드의 배경 전환(120~150ms)이 옛 색에서 출발해
 *  잠깐 흰 면이 남거나, 백그라운드 탭에서는 그 상태로 멈춰 보였다. */
function freezeTransitions(): void {
  const root = document.documentElement;
  root.classList.add('theme-switching');
  window.setTimeout(() => root.classList.remove('theme-switching'), 250);
}

/** 휴대폰 브라우저 상단 · 설치 앱 상태 표시줄 색(meta theme-color)을 상단바 표면에 맞춘다.
 *  meta 는 CSS 변수를 읽지 못해 --surface 의 두 값을 그대로 둔다(index.html 과 같은 값). */
const THEME_COLOR = { light: '#ffffff', dark: '#13161b' } as const;
function syncThemeColor(t: ThemeChoice): void {
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((m) => {
    const media = m.getAttribute('media') ?? '';
    const sys = media.includes('dark') ? THEME_COLOR.dark : THEME_COLOR.light;
    m.content = t === 'system' ? sys : THEME_COLOR[t];
  });
}

function apply(t: ThemeChoice): void {
  const root = document.documentElement;
  freezeTransitions();
  syncThemeColor(t);
  if (t === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', t);
  try {
    if (t === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, t);
  } catch { /* 저장만 못 할 뿐 화면에는 적용된다 */ }
}

export default function ThemeToggle() {
  const [theme, setTheme] = useState<ThemeChoice>(read);

  // 다른 탭에서 바꾼 테마를 따라간다
  useEffect(() => {
    const on = (e: StorageEvent) => {
      if (e.key !== KEY) return;
      const t = read();
      setTheme(t);
      apply(t);
    };
    window.addEventListener('storage', on);
    // 시스템 설정이 바뀔 때(시스템 모드)도 전환 효과 없이 한 번에 바꾼다
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onScheme = () => freezeTransitions();
    mq?.addEventListener?.('change', onScheme);
    return () => {
      window.removeEventListener('storage', on);
      mq?.removeEventListener?.('change', onScheme);
    };
  }, []);

  const next = ORDER[(ORDER.indexOf(theme) + 1) % ORDER.length];
  const label = `화면 테마: ${LABEL[theme]} — 누르면 ${TO[next]} 바뀝니다`;

  return (
    <button type="button" className="icon-btn theme-btn" aria-label={label} title={label}
            onClick={() => { apply(next); setTheme(next); }}>
      <Icon name={ICON[theme]} size={18} />
    </button>
  );
}
